import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { PageHeader } from '../components/Section';
import { format, useTranslation } from '../i18n';

type PricingRow = {
  id: number;
  endpoint: string;
  modelName: string;
  resolutionKey: string;
  durationKey: string;
  credits: number;
  effectiveFrom: string;
};

type DraftRow = {
  endpoint: string;
  model_name: string;
  resolution_key: string;
  duration_key: string;
  credits: string;
};

// '*' means "any" in the lookup — render it as a muted chip so the table reads
// at a glance.
function KeyCell({ value }: { value: string }) {
  const t = useTranslation();
  if (value === '*') {
    return (
      <Chip
        size="small"
        label={t.adminPricing.any}
        variant="outlined"
        sx={{ height: 20, fontSize: 11, color: 'text.disabled', borderStyle: 'dashed' }}
      />
    );
  }
  return (
    <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
      {value}
    </Typography>
  );
}

// Resolution keys are stored as "480p" / "720p" / "1080p". Admins often type
// the bare number; add the missing "p" so the row actually matches lookups
// (the server applies the same rule).
export function normalizeResolutionKey(raw: string): string {
  const s = raw.trim();
  if (!s) return '*';
  const m = /^(\d{3,4})\s*[pP]?$/.exec(s);
  return m ? `${m[1]}p` : s;
}

function PriceDialog({
  existing,
  onClose,
  onSaved,
}: {
  existing: PricingRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const isEdit = !!existing;
  const [draft, setDraft] = useState<DraftRow>({
    endpoint: existing?.endpoint ?? '',
    model_name: existing?.modelName ?? '*',
    resolution_key: existing?.resolutionKey ?? '*',
    duration_key: existing?.durationKey ?? '*',
    credits: existing != null ? String(existing.credits) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof DraftRow) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));

  const handleSave = async () => {
    const endpoint = draft.endpoint.trim();
    if (!endpoint) {
      setError(t.adminPricing.dialog.endpointRequired);
      return;
    }
    const n = Number(draft.credits);
    if (!Number.isInteger(n) || n < 0) {
      setError(t.adminPricing.dialog.creditsInvalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/pricing', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint,
          model_name: draft.model_name.trim() || '*',
          resolution_key: normalizeResolutionKey(draft.resolution_key),
          duration_key: draft.duration_key.trim() || '*',
          credits: n,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || format(t.adminPricing.requestFailed, { status: res.status }));
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? t.adminPricing.dialog.editTitle : t.adminPricing.dialog.addTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t.adminPricing.dialog.intro}
          </Typography>
          <TextField
            label={t.adminPricing.dialog.endpoint}
            value={draft.endpoint}
            onChange={set('endpoint')}
            size="small"
            disabled={isEdit}
            autoFocus={!isEdit}
            placeholder={t.adminPricing.dialog.endpointPlaceholder}
            helperText={isEdit ? t.adminPricing.dialog.endpointHelperEdit : t.adminPricing.dialog.endpointHelperAdd}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label={t.adminPricing.dialog.model}
              value={draft.model_name}
              onChange={set('model_name')}
              size="small"
              disabled={isEdit}
              fullWidth
            />
            <TextField
              label={t.adminPricing.dialog.resolution}
              value={draft.resolution_key}
              onChange={set('resolution_key')}
              onBlur={() => setDraft((d) => ({ ...d, resolution_key: normalizeResolutionKey(d.resolution_key) }))}
              size="small"
              disabled={isEdit}
              fullWidth
              placeholder={t.adminPricing.dialog.resolutionPlaceholder}
            />
            <TextField
              label={t.adminPricing.dialog.duration}
              value={draft.duration_key}
              onChange={set('duration_key')}
              size="small"
              disabled={isEdit}
              fullWidth
              placeholder={t.adminPricing.dialog.durationPlaceholder}
            />
          </Stack>
          <TextField
            label={t.adminPricing.dialog.credits}
            type="number"
            value={draft.credits}
            onChange={set('credits')}
            size="small"
            autoFocus={isEdit}
            helperText={t.adminPricing.dialog.creditsHelper}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t.common.cancel}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || draft.credits === ''}>
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t.common.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Pricing() {
  const t = useTranslation();
  const [rows, setRows] = useState<PricingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PricingRow | null>(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const res = await fetch('/api/v1/admin/pricing', { credentials: 'include' });
      if (!res.ok) throw new Error(format(t.adminPricing.loadError, { status: res.status }));
      const data = (await res.json()) as { items: PricingRow[] };
      setRows(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleDelete = async (row: PricingRow) => {
    if (!confirm(format(t.adminPricing.deleteConfirm, { endpoint: row.endpoint }))) return;
    try {
      const res = await fetch(`/api/v1/admin/pricing/${row.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(format(t.adminPricing.deleteFailed, { status: res.status }));
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Box>
      <PageHeader title={t.adminPricing.title} description={t.adminPricing.description} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          {t.adminPricing.addPrice}
        </Button>
      </Stack>

      {rows == null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t.adminPricing.table.endpoint}</TableCell>
                  <TableCell>{t.adminPricing.table.model}</TableCell>
                  <TableCell>{t.adminPricing.table.resolution}</TableCell>
                  <TableCell>{t.adminPricing.table.duration}</TableCell>
                  <TableCell align="right">{t.adminPricing.table.credits}</TableCell>
                  <TableCell align="right">{t.adminPricing.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.endpoint}
                      </Typography>
                    </TableCell>
                    <TableCell><KeyCell value={r.modelName} /></TableCell>
                    <TableCell><KeyCell value={r.resolutionKey} /></TableCell>
                    <TableCell><KeyCell value={r.durationKey} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {r.credits}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t.adminPricing.editCredits} arrow>
                        <IconButton size="small" onClick={() => setEditing(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.adminPricing.delete} arrow>
                        <IconButton size="small" onClick={() => void handleDelete(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {t.adminPricing.empty}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {(adding || editing) && (
        <PriceDialog
          existing={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            void load();
          }}
        />
      )}
    </Box>
  );
}
